import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Card } from './components/Card';
import { PageHeader, Shell } from './components/Shell';
import { NavIcon } from './components/NavIcon';
import { navLabels } from './components/navLabels';
import {
  demoKey,
  demoLabel,
  demoPath,
  homePath,
  joinPath,
  pathFor,
  routeFor,
  routes,
} from './routes';
import type { SessionUser } from './net/session';
import { getUnreadCount } from './net/alerts';
import { getOrganization, getReadiness, toGo } from './net/brandProfile';
import { getOpenCount } from './net/productReports';
import { identityOf } from './session/identity';
import { OpenReportsContext } from './session/openReports';
import { ProfileGapsContext } from './session/profileGaps';
import { UnreadAlertsContext } from './session/unreadAlerts';
import { useSession, useSessionState } from './session/useSession';
import { Alerts } from './screens/Alerts';
import { ApiKeys } from './screens/ApiKeys';
import { BrandProfile } from './screens/BrandProfile';
import { CodeBatches } from './screens/CodeBatches';
import { ConsumerReports } from './screens/ConsumerReports';
import { Dashboard } from './screens/Dashboard';
import { DemoSamples } from './screens/DemoSamples';
import { Join } from './screens/Join';
import { ProductForm } from './screens/ProductForm';
import { Products } from './screens/Products';
import { Regions } from './screens/Regions';
import { Reports } from './screens/Reports';
import { Restoring, SignIn, Unreachable } from './screens/SignIn';
import { Team } from './screens/Team';

/**
 * The route table, behind the shell.
 *
 * A route renders its screen once the unit that builds it has landed, and the
 * placeholder panel until then. `screens` is that list. The
 * `*` route sends `/` and any unknown path to the dashboard with `replace`, so
 * a bad link does not leave a dead entry in history. The artboards draw no not-found
 * page, so none is drawn here, see D4.
 *
 * The router itself is supplied by the caller: `BrowserRouter` in `main.tsx`,
 * a memory router in tests. So is the session, through `SessionProvider`.
 *
 * Nothing renders behind the shell until a session is held. No portal board
 * draws sign-in, a restoring state or an unreachable API, so each is the
 * mobile app's, carried as D20 rules.
 */
export function App() {
  const state = useSessionState();
  const { pathname } = useLocation();
  if (state.status === 'restoring') return <Restoring />;
  if (state.status === 'signed_out') return matchPath(joinPath, pathname) ? <Join /> : <SignIn />;
  if (state.status === 'unreachable') return <Unreachable />;
  return (
    <Routes>
      <Route element={<Frame user={state.user} />}>
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={screens[route.path] ?? <NotBuilt title={route.title} />}
          />
        ))}
        <Route path={demoPath} element={<DemoSamples />} />
        <Route path="*" element={<Navigate to={homePath} replace />} />
      </Route>
    </Routes>
  );
}

/** The screens that are built, by route path. */
const screens: Record<string, ReactNode> = {
  '/dashboard': <Dashboard />,
  '/consumer-reports': <ConsumerReports />,
  '/alerts': <Alerts />,
  '/products': <Products />,
  '/products/new': <ProductForm />,
  '/code-batches': <CodeBatches />,
  '/reports': <Reports />,
  '/brand-profile/regions': <Regions />,
  '/team': <Team />,
  '/api-keys': <ApiKeys />,
  '/brand-profile': <BrandProfile />,
};

/**
 * The shell. The URL, not component state, says which row is active.
 *
 * The Consumer reports badge is the list route's `openCount`, read once here
 * and corrected by the inbox, D13; the Alerts badge is `unreadCount`, the same
 * way, D14. The Brand profile badge is the readiness checklist's open rows,
 * the same way again, D17. The demo row is added after Code batches only when
 * the organization reads `demoMode`, D36; a failed read leaves it off. A page
 * that has already set its count wins over a badge read that lands after it.
 * A failed read leaves its badge off: it is a hint, and the page says the
 * same number in its header.
 */
function Frame({ user }: { user: SessionUser }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const { api } = session;
  const [demoMode, setDemoMode] = useState(false);
  const drawn = Object.keys(navLabels);
  const keys = demoMode
    ? [
        ...drawn.slice(0, drawn.indexOf('code-batches') + 1),
        demoKey,
        ...drawn.slice(drawn.indexOf('code-batches') + 1),
      ]
    : drawn;
  const [openCount, setOpenCount] = useState<number | null>(null);
  const openReports = useMemo(() => ({ count: openCount, set: setOpenCount }), [openCount]);
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const unreadAlerts = useMemo(() => ({ count: unreadCount, set: setUnreadCount }), [unreadCount]);
  const [gapCount, setGapCount] = useState<number | null>(null);
  const profileGaps = useMemo(() => ({ count: gapCount, set: setGapCount }), [gapCount]);

  useEffect(() => {
    const controller = new AbortController();
    getOpenCount(api, controller.signal).then(
      (count) => setOpenCount((held) => held ?? count),
      () => {},
    );
    getUnreadCount(api, controller.signal).then(
      (count) => setUnreadCount((held) => held ?? count),
      () => {},
    );
    getOrganization(api, controller.signal)
      .then((organization) => organization.demoMode === true)
      .then(setDemoMode, () => {});
    getReadiness(api, controller.signal)
      .then(toGo)
      .then(
        (count) => setGapCount((held) => held ?? count),
        () => {},
      );
    return () => controller.abort();
  }, [api]);

  return (
    <Shell
      surface="portal"
      eyebrow="MANUFACTURER PORTAL"
      nav={keys.map((key) => ({
        key,
        label: navLabels[key] ?? demoLabel,
        icon: <NavIcon name={key} />,
        ...(key === 'consumer-reports' && openCount ? { badge: openCount } : {}),
        ...(key === 'alerts' && unreadCount ? { badge: unreadCount } : {}),
        ...(key === 'brand-profile' && gapCount ? { badge: gapCount } : {}),
      }))}
      activeKey={routeFor(pathname)?.nav ?? (matchPath(demoPath, pathname) ? demoKey : '')}
      onNavigate={(key) => {
        const to = pathFor(key);
        // Clicking the row already open should not stack a duplicate entry.
        if (to !== pathname) navigate(to);
      }}
      identity={identityOf(user)}
      onSignOut={() => void session.signOut()}
    >
      <OpenReportsContext.Provider value={openReports}>
        <UnreadAlertsContext.Provider value={unreadAlerts}>
          <ProfileGapsContext.Provider value={profileGaps}>
            <Outlet />
          </ProfileGapsContext.Provider>
        </UnreadAlertsContext.Provider>
      </OpenReportsContext.Provider>
    </Shell>
  );
}

/**
 * The shell, with nothing in it yet.
 *
 * The frame is drawn by every artboard in `design/` and is built. The screens
 * are not: each one is a unit in docs/PRODUCTION-PLAN.md and each is built,
 * verified and documented in its own session. This placeholder says so out
 * loud rather than showing a convincing empty dashboard, because a screen that
 * looks finished and is not is the expensive kind of wrong.
 *
 * Replace it route by route as each screen unit lands.
 */
function NotBuilt({ title }: { title: string }) {
  return (
    <>
      <PageHeader title={title} subtitle="Nothing is wired yet" />
      <Card
        title="This screen is not built"
        body="The shell, the tokens and the type scale are built and tested. Every screen is a unit in docs/PRODUCTION-PLAN.md, built one per session against its artboard in design/."
      />
    </>
  );
}
